import { Component, inject, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LazyLoadEvent, MenuItem, MessageService, SelectItem } from 'primeng/api';
import { Observable, Subject, Subscription } from 'rxjs';
import { SERVICE_ENDPOINTS } from '../../../core/configs/service-endpoints';
import { ServiceResult } from '../../../core/interfaces/service-result';
import { AuthService } from '../../../core/services/auth.service';
import { HelperService } from '../../../core/services/helper.service';
import { I18nService } from '../../../core/services/i18n.service';
import { UserSessionSettingsProvider } from '../../../core/services/user-session-settings-provider.service';
import { UserService } from '../../../core/services/user.service';
import { SpsTableComponent } from '../../../custom/components/sps-table/sps-table.component';
import { TableConfig } from '../../../custom/interfaces/sps-table/table-config';
import { TableListProvider } from '../../../custom/provider/table-list-provider';
import { TableConfigService } from '../../../custom/services/table-config.service';
import { TableControlService } from '../../../custom/services/table-control.service';
import { LayoutService } from '../../../layout/services/layout.service';
import { OrganisationData } from '../../interfaces/organisation/organisation-data';
import { OrganisationDataInvalidFlags } from '../../interfaces/organisation/organisation-data-invalid-flags';
import { PaymentData } from '../../interfaces/payment/payment-data';
import { UserData } from '../../interfaces/user-data';
import { CustomerDataService } from '../../services/customer-data.service';
import { PaymentDataService } from '../../services/payment-data.service';
import { TransactionDataService } from '../../services/transaction-data.service';
import { BaseComponent } from './base.component';

@Component({
  template: '',
})
export abstract class BaseAdminCustomerComponent extends BaseComponent {

  /**
   * Services-Injection
   */
  router = inject(Router);
  route = inject(ActivatedRoute);
  i18n = inject(I18nService);
  helper = inject(HelperService);
  protected messageService = inject(MessageService);
  layoutService = inject(LayoutService);
  protected userSessionSettingsProvider = inject(UserSessionSettingsProvider);
  protected tableConfigService = inject(TableConfigService);
  tableControlService = inject(TableControlService);
  protected customerDataService = inject(CustomerDataService);
  protected paymentDataService = inject(PaymentDataService);
  protected transactionDataService = inject(TransactionDataService);
  authService = inject(AuthService);
  userService = inject(UserService);

  /**
   * redirect for the table refresh workaround when body is not rendered after filter-reset
   */
  redirectRoute: string = ''

  /**
   * table-config for organisations-table
   */
  readonly tableConfig: WritableSignal<TableConfig> = signal(null);
  totalRecords: number = 0;
  rows: number = 20;
  scrollHeightOffset: number = 146;
  minScrollHeight: number = 160;
  tableListProvider: TableListProvider;
  selectedRowData: OrganisationData = null;
  selectedRowDataBackup: OrganisationData = null;
  tableReference: SpsTableComponent  = null;

  /**
   * Params for Data-Refresh-Polling
   */
  lastLazyLoadingEvent: LazyLoadEvent = null;

  /**
   * Admin split-menu Button
   * @private
   */
  adminMenuActive: boolean = false;
  adminMenuItems: MenuItem[] = [];

  /**
   * Details-Dialog
   */
  showOrganisationDetailsDialog: boolean = false;
  organisationPaymentTypes: PaymentData[] = [];
  organisationDataInvalidFlags: OrganisationDataInvalidFlags;

  /**
   * Customer Data Dialog
   * with organisation specific payment types
   */
  showCustomerDataDialog: boolean = false;
  organisationPaymentTypesFetched = new Subject<boolean>()

  /**
   * list of all organizations that ar marked as referral partner
   */
  referralPartnerList: SelectItem[] = []

  /**
   * list of all organizations that ar marked as data center
   */
  dataCenterList: SelectItem[] = []

  /**
   * Status change dialog
   * @protected
   */
  showStatusChangeDialog: boolean = false;
  newStatusList: SelectItem[] = [];

  /**
   * abstract class should be implemented in child
   * @param organisationData
   */
  abstract onSelectionChanged(organisationData: OrganisationData): void;

  onTableInitialized(dataTable: SpsTableComponent) {
    this.tableReference = dataTable;
  }

  /**
   * Table-data-Fetched subscription
   * @param serviceResult
   */
  onDataFetched(serviceResult: ServiceResult) {
    this.totalRecords = serviceResult.totalElements;
  }

  /**
   * show organisation details dialog
   */
  showDetailsDialog(): void {
    if (this.selectedRowData) {
      const serviceEndpoint = SERVICE_ENDPOINTS.GET_USERS_ORG_INIT + this.selectedRowData.zbvOrganisationId + '/initial-user';
      this.userService.findAll(serviceEndpoint).subscribe({
        next: () => {
          this.getPaymentTypes();
        },
        error: err => {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
          this.getPaymentTypes();
        }
      });
    }
  }

  /**
   * get payment types of the organisation
   * @private
   */
  private getPaymentTypes() {
    const subscription: Subscription = this.getCustomerPaymentTypes().subscribe({
      next: result => {
        subscription.unsubscribe();
        this.initOrganisationDataInvalidFlags();
        this.showOrganisationDetailsDialog = true;
      }
    })
  }

  /**
   * Close Organisation Details Dialog
   */
  closeDetailsDialog() {
    this.showOrganisationDetailsDialog = false;
  }

  /**
   * Close Customer Data Dialog
   * Check for chnages
   */
  closeCustomerDataDialog() {
    this.selectedRowData = this.helper.jsonCopy(this.selectedRowDataBackup);
    this.showCustomerDataDialog = false;
    this.tableControlService.refreshTableData.set(true);
  }

  /**
   * Clear all Table Filters and Sorting and reset the table state and the selection
   * @protected
   */
  protected clearTableFilters() {
    this.tableControlService.clearState.set(true);
    this.tableControlService.clearTableFilters.set(true);
    this.onSelectionChanged(null);
    /**
     * remove if filter reset problem is cleared
     * sometimes the primeNG table does not render the body after the reset of the filters
     * it seems to be a problem by signals and effect
     * so as a workaround - routing to another component and routing back refreshes the table component
     */
    // this.router.navigate(['/refreshworkaround/' + this.redirectRoute])
  }

  /**
   * db-persistence of the organisation-data
   */
  protected saveOrganisationData(resetRowSelection: boolean = false) {
    this.selectedRowData = this.cleanOrganisationData(this.selectedRowData);
    this.customerDataService.update(SERVICE_ENDPOINTS.UPDATE_ORGANISATIONS_ADMIN + this.selectedRowData.zbvOrganisationId, this.selectedRowData).subscribe({
      next: (result) => {
        if (result.successful) {
          this.showStatusChangeDialog = false;
          this.showCustomerDataDialog = false;
          if (resetRowSelection) {
            this.onSelectionChanged(null);
          } else {
            this.onSelectionChanged(result.dto);
          }
          /**
           * refresh referral-partner list
           */
          const changedOrganisationData = (result.dto as OrganisationData);
          if (changedOrganisationData.isReferralPartner) {
            this.referralPartnerList = this.updateResolverDataList(this.referralPartnerList, {value: changedOrganisationData.id, label: changedOrganisationData.name}, 'REFERRALPARTNERLIST', 'ADD');
          } else {
            this.referralPartnerList = this.updateResolverDataList(this.referralPartnerList, {value: changedOrganisationData.id, label: changedOrganisationData.name}, 'REFERRALPARTNERLIST', 'DELETE');
          }
          /**
           * refresh data-center list
           */
          if (changedOrganisationData.isReferralPartner) {
            this.dataCenterList = this.updateResolverDataList(this.dataCenterList, {value: changedOrganisationData.id, label: changedOrganisationData.name}, null, 'ADD');
          } else {
            this.dataCenterList = this.updateResolverDataList(this.dataCenterList, {value: changedOrganisationData.id, label: changedOrganisationData.name}, null, 'DELETE');
          }
          /**
           * refresh table
           */
          this.tableControlService.refreshTableData.set(true);
          this.messageService.add({
            life: 3500,
            severity: 'success',
            summary: this.i18n.i18nLng()['EDITCUSTOMERDATA'],
            detail: this.i18n.i18nLng()['HTTPDATAUPDATESUCCESS']
          });
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: result.message,
            detail: result.details
          });
        }
      },
      error: (error) => {
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: error.message
        });
      }
    });
  }

  /**
   * Clean regex data fields - remove spaces on end
   * @param organisationData
   * @private
   */
  private cleanOrganisationData(organisationData): OrganisationData {
    if (organisationData?.vatId) {
      organisationData.vatId = organisationData.vatId.trimEnd();
    }
    if (organisationData?.creditorId) {
      organisationData.creditorId = organisationData.creditorId.trimEnd();
    }
    return organisationData;
  }

  /**
   * Initialization of the flags for the required-fields check
   * @protected
   */
  protected initOrganisationDataInvalidFlags() {
    this.organisationDataInvalidFlags = {
      customerType: false,
      organisationType: false,
      customerGroup: false,
      streetNr: false,
      postalCode: false,
      city: false,
      email: false,
      vatId: false,
      creditorId: false,
      websiteUrl: false,
      paymentMethod: false,
      mandateReference: false,
    }
  }

  /**
   * Organisation data in dialog has changed and was validated
   * @param valid
   * @protected
   */
  protected onDataValidated(valid: boolean) {
    this.dataValid = valid;
  }

  /**
   * fetch the payment types for the selected organisation
   * @protected
   */
  protected getCustomerPaymentTypes(): Observable<boolean> {
    this.organisationPaymentTypes = [];
    this.paymentDataService.findPageAdmin(this.helper.eagerLazyLoadEvent(), this.selectedRowData.zbvOrganisationId).subscribe({
      next: result => {
        if (result.successful) {
          this.organisationPaymentTypes = result.payload;
          this.organisationPaymentTypesFetched.next(true);
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
          this.organisationPaymentTypesFetched.next(false);
        }
      },
      error: err => {
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
        });
        this.organisationPaymentTypesFetched.next(false);
      }
    })
    return this.organisationPaymentTypesFetched.asObservable();
  }

  /**
   * Update a Resolver Data-List in memory
   * @param list
   * @param changedItem
   * @param providerKey
   * @param updateType
   * @protected
   */
  private updateResolverDataList(list: SelectItem[], changedItem: SelectItem, providerKey: string, updateType: 'ADD' | 'DELETE'): SelectItem[] {
    if (updateType === 'ADD') {
      if (!list.find(item => item.value === changedItem.value)) {
        list.push(changedItem);
        if (providerKey !== null) {
          this.tableListProvider.addListByKey(providerKey, list);
        }
      }
    }
    if (updateType === 'DELETE') {
      if (list.find(item => item.value === changedItem.value)) {
        list = list.filter(item => item.value !== changedItem.value);
        if (providerKey !== null) {
          this.tableListProvider.addListByKey(providerKey, list);
        }
      }
    }
    return list;
  }

}
